import React, { createContext, useContext, useState } from 'react';
import { useAuthenticatedEffect } from '../hooks/useAuthenticatedEffect';
import { authenticatedFetch } from '../services/authService';

type StatusKey = 'Potwierdzone' | 'Realizacja' | 'Zakonczone' | 'Anulowane';

export type StatusColors = (
  Record<StatusKey, { bg: string; hoverBg: string; text: string; border: string; rowBg: string }>
  & {
    Tabs: { activeBorder: string; activeBg: string; activeText: string; inactiveBorder: string; inactiveBg: string; inactiveText: string };
    WezelButton: { bg: string; hoverBg: string; text: string; border: string; disabledBg: string; disabledText: string };
    Aggregates: { zam: string; wykonano: string; pozostalo: string; separator: string };
  }
)

const defaultColors: StatusColors = {
  Potwierdzone: { bg: '#90caf9', hoverBg: '#64b5f6', text: '#1565c0', border: '#42a5f5', rowBg: 'rgba(144,202,249,0.10)' },
  Realizacja:   { bg: '#fff59d', hoverBg: '#fff176', text: '#f57f17', border: '#ffeb3b', rowBg: 'rgba(255,245,157,0.22)' },
  Zakonczone:   { bg: '#a5d6a7', hoverBg: '#81c784', text: '#2e7d32', border: '#66bb6a', rowBg: 'rgba(165,214,167,0.18)' },
  Anulowane:    { bg: '#bdbdbd', hoverBg: '#9e9e9e', text: '#424242', border: '#757575', rowBg: 'rgba(189,189,189,0.16)' },
  Tabs: {
    activeBorder: '#1976D2', activeBg: 'rgba(25,118,210,0.08)', activeText: '#0D47A1',
    inactiveBorder: '#E0E0E0', inactiveBg: '#F7F7F7', inactiveText: '#424242'
  },
  WezelButton: {
    bg: '#E3B6AA', hoverBg: '#eef7f3', text: '#4a5568', border: '#4a5568', disabledBg: '#f1f4f8', disabledText: '#adb5bd'
  },
  Aggregates: {
    zam: '#1976d2',
    wykonano: '#2e7d32',
    pozostalo: '#d32f2f',
    separator: '#9e9e9e'
  }
};

const Ctx = createContext<StatusColors>(defaultColors);

export const StatusColorsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [colors, setColors] = useState<StatusColors>(defaultColors);

  // Derive row background from base bg if not provided in preferences
  const ensureRowBg = (input: any): StatusColors => {
    const toRgb = (val: string): { r: number; g: number; b: number } => {
      try {
        if (!val) return { r: 0, g: 0, b: 0 };
        const hex = val.trim();
        if (hex.startsWith('#')) {
          const h = hex.slice(1);
          const norm = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
          const r = parseInt(norm.slice(0, 2), 16);
          const g = parseInt(norm.slice(2, 4), 16);
          const b = parseInt(norm.slice(4, 6), 16);
          return { r, g, b };
        }
        const m = hex.match(/rgba?\((\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
        if (m) return { r: parseInt(m[1], 10), g: parseInt(m[2], 10), b: parseInt(m[3], 10) };
      } catch {}
      return { r: 0, g: 0, b: 0 };
    };
    const derive = (bg?: string, fallback?: string) => {
      if (typeof fallback === 'string' && fallback.startsWith('rgba(')) return fallback;
      const { r, g, b } = toRgb(bg || '#000000');
      // Slight transparency for row highlight
      return `rgba(${r},${g},${b},0.18)`;
    };
    const mergeStatus = (key: 'Potwierdzone' | 'Realizacja' | 'Zakonczone' | 'Anulowane') => {
      const base = (input && input[key]) || {};
      const rowBg = typeof base.rowBg === 'string' && base.rowBg ? base.rowBg : derive(base.bg, (defaultColors as any)[key].rowBg);
      return { ...((defaultColors as any)[key]), ...base, rowBg };
    };
    const out: StatusColors = {
      Potwierdzone: mergeStatus('Potwierdzone'),
      Realizacja: mergeStatus('Realizacja'),
      Zakonczone: mergeStatus('Zakonczone'),
      Anulowane: mergeStatus('Anulowane'),
      Tabs: { ...defaultColors.Tabs, ...(input?.Tabs || {}) },
      WezelButton: { ...defaultColors.WezelButton, ...(input?.WezelButton || {}) },
      Aggregates: { ...defaultColors.Aggregates, ...(input?.Aggregates || {}) }
    } as StatusColors;
    return out;
  };

  const loadPrefs = async (cancelledRef?: { v: boolean }) => {
    try {
      const api = await authenticatedFetch('/api/preferences?namespace=ui.statusColors', { credentials: 'include', cache: 'no-store' });
      if (api.ok) {
        const pref = await api.json();
        if (pref && pref.success && pref.value && typeof pref.value === 'object') {
          if (!cancelledRef?.v) setColors(ensureRowBg(pref.value));
          return;
        }
      }
      const res = await authenticatedFetch('/config/status-colors.json', { cache: 'no-store' });
      if (res.ok) {
        const json = await res.json();
        if (json && typeof json === 'object') {
          if (!cancelledRef?.v) setColors(ensureRowBg(json));
          return;
        }
      }
    } catch {}
  };

  // Load preferences only after authentication is complete
  useAuthenticatedEffect(() => {
    const c = { v: false };
    loadPrefs(c);
    return () => { c.v = true; };
  }, []);

  return <Ctx.Provider value={colors}>{children}</Ctx.Provider>;
};

export const useStatusColors = () => useContext(Ctx);


